/**
 * Histopathology – Demographics Layout Configuration.
 * Migrated from admin4.0 DemographicsConfigHome.js (functional component).
 */
import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Typography,
  Grid,
  Checkbox,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
} from "@mui/material";
import "../../Styles/Configuration.css";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { Demo_Layout_Default_JSON } from "../../Utility/Constants";
import { ImagePaths } from "../../Utility/ImagePaths";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { CheckAccessFunc } from "../../Components/Common Components/DashboardComponents";
import Loader from "../../Components/Loader";

/** field_name → label for API field_order mapping */
const FIELD_LABEL_MAP = Object.fromEntries(
  Demo_Layout_Default_JSON.map((f) => [f.field_name, f.label])
);
const DEMO_FIELD_ORDER_URL = Serviceurls.HISTOPATHOLOGY_DEMOGRAPHIC_FIELD_ORDER;

const LAYOUT_ACCESS = () =>
  CheckAccessFunc("Histopathology", "Settings", "Layout Configuration", "Demographics Configuration", "Tab");

const COUNT_OPTIONS = ["03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15"];

export default function DemographicsLayout() {
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const [mandotory_count, setMandotory_count] = useState("03");
  const [right_Fields_data, setRight_Fields_data] = useState(Demo_Layout_Default_JSON);
  const [drag_label, setDrag_label] = useState(null);
  const [layout_id, setLayout_id] = useState(null);
  const [isLoader, setIsLoader] = useState(false);

  const editAccess = LAYOUT_ACCESS()?.editAccess ?? false;

  const errorMessage = useCallback((message) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(message);
  }, []);

  const successMessage = useCallback((message) => {
    setSuccessMsg(true);
    setSuccessMsgText(message);
  }, []);

  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  }, []);

  const getDemoLayout = useCallback(() => {
    setIsLoader(true);
    RestAPIService.getAll(DEMO_FIELD_ORDER_URL)
      .then((response) => {
        if (response?.data?.status === "success") {
          const Data = response.data.data || [];
          const len = Data.length;
          const last = Data[len - 1];
          setLayout_id(last ? last.id : null);
          if (last?.field_order?.length) {
            const uiFields = last.field_order
              .slice()
              .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
              .map((f) => ({
                id: f.index,
                field_name: f.field_name,
                label: FIELD_LABEL_MAP[f.field_name] ?? f.field_name,
                is_active: true,
                is_default: !!f.is_mandatory,
              }));
            setRight_Fields_data(uiFields);
          } else {
            setRight_Fields_data(Demo_Layout_Default_JSON);
          }
          const count = last?.mandatory_field_count;
          setMandotory_count(
            count != null
              ? count < 10
                ? "0" + count
                : String(count)
              : "03"
          );
        }
        setIsLoader(false);
      })
      .catch((error) => {
        setIsLoader(false);
        errorMessage(error?.response?.data?.message || error?.message);
      });
  }, [errorMessage]);

  useEffect(() => {
    getDemoLayout();
  }, [getDemoLayout]);

  const postDemoLayout = useCallback(() => {
    const payload = {
      mandatory_field_count: +(mandotory_count),
      field_order: right_Fields_data.map((item) => ({
        field_name: item.field_name,
        index: item.id,
        is_mandatory: !!item.is_default,
      })),
    };
    const doSave = layout_id
      ? RestAPIService.create(
          { id: layout_id, ...payload },
          DEMO_FIELD_ORDER_URL
        )
      : RestAPIService.create(payload, DEMO_FIELD_ORDER_URL);

    doSave
      .then((response) => {
        if (response?.data?.status === "success") {
          successMessage(response.data.message);
          if (response?.data?.id) setLayout_id(response.data.id);
          else if (layout_id) setLayout_id(layout_id);
          getDemoLayout();
        }
      })
      .catch((error) => {
        errorMessage(error?.response?.data?.message || error?.message);
      });
  }, [
    mandotory_count,
    right_Fields_data,
    layout_id,
    successMessage,
    errorMessage,
    getDemoLayout,
  ]);

  const renderInput = (data, type) => (
    <Box
      key={data.id}
      sx={{
        marginTop: "2.5vh",
        marginLeft: "1vw",
        width:
          data.label === "Age" || data.label === "Title" || data.label === "Gender"
            ? "5.5vw"
            : "12vw",
      }}
    >
      <TextField
        disabled
        label={
          type === "mandatory" || data.is_default ? data.label + "*" : data.label
        }
        variant="outlined"
        size="small"
        defaultValue=" "
      />
    </Box>
  );

  const OnChangeCount = (e) => {
    const val = e.target.value;
    let defaultCount = 0;
    right_Fields_data.forEach((item) => {
      if (item.is_default === true && item.id <= +val) defaultCount += 1;
    });
    if (defaultCount === 3) {
      setMandotory_count(val);
    } else {
      errorMessage("Default Mandatory Fields are only Within Mandatory Counts");
    }
  };

  const handleDrag = (e) => {
    setDrag_label(e.currentTarget.id);
  };

  const handleDrop = (e) => {
    const dragBox = right_Fields_data.find((f) => f.label === drag_label);
    const dropBox = right_Fields_data.find((f) => f.label === e.currentTarget.id);
    if (!dragBox || !dropBox) return;

    const dragBoxID = dragBox.id;
    const dropBoxID = dropBox.id;
    const dragBoxField = dragBox.field_name;
    const dropBoxField = dropBox.field_name;
    const dragBoxCheck = dragBox.is_active;
    const dropBoxCheck = dropBox.is_active;
    const dragBoxDefault = dragBox.is_default;
    const dropBoxDefault = dropBox.is_default;
    const splitCount = +mandotory_count;

    let newBoxState = right_Fields_data.map((field) => {
      if (
        (!dragBoxDefault && !dropBoxDefault) ||
        (dragBoxDefault && dropBoxDefault)
      ) {
        if (field.label === drag_label) {
          return {
            ...field,
            id: dropBoxID,
            field_name: dragBoxField,
            is_active: dragBoxCheck,
            is_default: dragBoxDefault,
          };
        }
        if (field.label === e.currentTarget.id) {
          return {
            ...field,
            id: dragBoxID,
            field_name: dropBoxField,
            is_active: dropBoxCheck,
            is_default: dropBoxDefault,
          };
        }
      } else if (
        dropBoxID <= splitCount &&
        dragBoxID <= splitCount
      ) {
        if (field.label === drag_label) {
          return {
            ...field,
            id: dropBoxID,
            field_name: dragBoxField,
            is_active: dragBoxCheck,
            is_default: dragBoxDefault,
          };
        }
        if (field.label === e.currentTarget.id) {
          return {
            ...field,
            id: dragBoxID,
            field_name: dropBoxField,
            is_active: dropBoxCheck,
            is_default: dropBoxDefault,
          };
        }
      } else {
        errorMessage(
          "Default Mandatory Fields are only dragable Within Mandatory Counts"
        );
      }
      return field;
    });

    const sortData = [...newBoxState].sort((a, b) => a.id - b.id);
    setRight_Fields_data(sortData);
  };

  const handleCheckClick = (e) => {
    const id = e.target.id;
    const index = right_Fields_data.findIndex((d) => String(d.id) === id);
    if (index === -1) return;
    if (right_Fields_data[index].is_default) return;
    const next = [...right_Fields_data];
    next[index] = { ...next[index], is_active: e.target.checked };
    setRight_Fields_data(next);
  };

  const renderRightField = (data) => (
    <div
      key={data.id}
      draggable
      onDragOver={(e) => e.preventDefault()}
      onDragStart={handleDrag}
      onDrop={handleDrop}
      id={data.label}
      className="eMed_demolayout_right2_field"
    >
      <img
        className="eMed_demolayout_right2_field_img"
        src={ImagePaths.DragIndicator?.default ?? ImagePaths.DragIndicator}
        alt=""
      />
      <FormControlLabel
        control={
          <Checkbox
            id={String(data.id)}
            onChange={handleCheckClick}
            checked={data.is_active}
          />
        }
        label={data.is_default ? data.label + "*" : data.label}
      />
    </div>
  );

  const splitCount = +(mandotory_count);
  const activeArray = right_Fields_data.filter((item) => item.is_active === true);
  const mandatoryArray = activeArray.slice(0, splitCount);
  const nonMandatoryArray = activeArray.slice(
    splitCount,
    right_Fields_data.length
  );
  const mandatoryCountNum = +(mandotory_count);

  return (
    <Box component="div" className="eMed_refdoc_container">
      <Grid container spacing={2} className="eMed_refdoc_content">
        <Grid item xs={8}>
          <Stack component="div">
            <Paper
              className="eMed_demolayout_left1"
              sx={{
                height:
                  mandatoryCountNum > 10 ? "39.3vh" : "24.8vh",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  backgroundColor: "#FAF9F9",
                }}
              >
                <Typography className="eMed_refdoc_title">
                  Mandatory Demographics Fields
                </Typography>
                <Typography className="emed_demo_left_title_right">
                  Preview
                </Typography>
              </Box>
              <Box className="eMed_demolayout_left_TextInput">
                {mandatoryArray.map((item) => renderInput(item, "mandatory"))}
              </Box>
            </Paper>
            <Paper
              className="eMed_demolayout_left2"
              sx={{
                height: mandatoryCountNum > 10 ? "35.5vh" : "50vh",
              }}
            >
              <Typography className="eMed_refdoc_title">
                Non Mandatory Demographics Fields
              </Typography>
              <Box
                className="eMed_demolayout_left_TextInput"
                sx={{
                  height: mandatoryCountNum > 10 ? "27vh" : "41vh",
                  overflowY: "scroll",
                }}
              >
                {nonMandatoryArray.map((item) =>
                  renderInput(item, "non-mandatory")
                )}
              </Box>
            </Paper>
          </Stack>
        </Grid>
        <Grid item xs={4}>
          <Stack component="div">
            <Paper className="eMed_demolayout_right1" elevation={2}>
              <Typography className="eMed_refdoc_title">
                Demographics
              </Typography>
              <Box className="emed_demolayout_right1_box">
                <Typography className="eMed_refdoc_title">
                  Mandatory Fields
                </Typography>
                <FormControl>
                  <Select
                    size="small"
                    value={mandotory_count}
                    autoWidth
                    onChange={OnChangeCount}
                    className="emed_demolayout_right1_select"
                    MenuProps={{ style: { maxHeight: 350 } }}
                  >
                    {COUNT_OPTIONS.map((value, index) => (
                      <MenuItem value={value} key={index}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  fontSize="0.75dvw"
                  color="#888888"
                  alignSelf="center"
                >
                  (System will take the first {+mandotory_count} fields as
                  mandatory fields)
                </Typography>
              </Box>
            </Paper>
            <Paper className="eMed_demolayout_right2" elevation={2}>
              {right_Fields_data.map((item) => renderRightField(item))}
            </Paper>
            <Paper className="eMed_demolayout_right3" elevation={2}>
              <Button
                variant="outlined"
                size="small"
                id="eMed_Config_btn"
                sx={{ mr: "0.5vw" }}
                onClick={() => {
                  setLayout_id(null);
                  setRight_Fields_data(Demo_Layout_Default_JSON);
                  setMandotory_count("03");
                }}
              >
                RESET
              </Button>
              <Button
                disabled={!editAccess}
                variant="contained"
                size="small"
                id="eMed_Config_btn"
                sx={{ ml: "0.5vw" }}
                onClick={postDemoLayout}
              >
                SAVE
              </Button>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
      {isErrorMsg && (
        <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />
      )}
      {successMsg && (
        <ToastMsg
          severity="success"
          msg={successMsgText}
          msgPop={msgClose}
        />
      )}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
